import type { ReactNode } from 'react';
import { useTranslations } from 'use-intl';
import type { AiAnswer, AiGrounding } from '@/app/queries';
import { Notice } from '@/ui/Panel';

/**
 * The frame every assistant's answer is shown in.
 *
 * The suggestion is never shown on its own. Why it was suggested, what it was
 * looked at, and what it could not know are rendered with it, every time, with
 * no way to collapse the last one — a coach deciding whether to run a session
 * on Tuesday needs to know the model never saw Monday's attendance, and that is
 * exactly the line a disclosure panel would hide.
 *
 * The cost sits in the footer for the same reason. A credit spent invisibly is
 * a credit someone discovers missing.
 */
export function AiAnswerFrame({
  answer,
  children,
  action,
}: {
  answer: AiAnswer<AiGrounding>;
  children: ReactNode;
  action?: ReactNode;
}) {
  const t = useTranslations();
  const { result } = answer;

  return (
    <div className="grid gap-4 border-t border-line bg-surface-sunken p-4">
      {children}

      <section>
        <h4 className="text-meta font-semibold text-ink-muted">{t('ai.why')}</h4>
        <p className="mt-1 max-w-[70ch] leading-relaxed">{result.why}</p>
      </section>

      {result.dataUsed.length > 0 ? (
        <section>
          <h4 className="text-meta font-semibold text-ink-muted">{t('ai.dataUsed')}</h4>
          <ul className="mt-1 grid gap-0.5 text-meta text-ink-muted">
            {result.dataUsed.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Never collapsible. The limits of an answer are part of the answer. */}
      <Notice>
        <span className="font-semibold">{t('ai.uncertainty')}</span> {result.uncertainty}
      </Notice>

      <p className="text-meta text-ink-faint">{t('ai.coachDecides')}</p>

      <div className="flex flex-wrap items-center gap-3 border-t border-line pt-3">
        <span className="text-meta text-ink-faint">
          {t('ai.spent', { credits: answer.credits, model: answer.model })}
        </span>
        <span className="tabular text-meta text-ink-faint">
          {t('ai.balanceLeft', { count: answer.balance })}
        </span>
        {action ? <span className="ms-auto">{action}</span> : null}
      </div>
    </div>
  );
}

/**
 * Why an assistant would not run, said in terms of what to do about it.
 *
 * Each refusal has a different person who can fix it and a different next step,
 * so they are not one "unavailable" message: a coach out of credits can buy
 * some now, a coach on the free plan needs to upgrade, and a coach who hit a
 * daily ceiling needs to know it resets rather than that something broke.
 */
export function AiRefusal({ code }: { code: string }) {
  const t = useTranslations();
  const known = new Set([
    'ai_switched_off',
    'ai_not_configured',
    'ai_not_in_plan',
    'insufficient_credits',
    'org_daily_limit',
    'platform_daily_limit',
    'model_declined',
    'answer_unusable',
  ]);
  return (
    <Notice tone="risk">
      {known.has(code) ? t(`ai.refused.${code}`) : t('ai.refused.unknown')}
    </Notice>
  );
}
